import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  Avatar,
  Chip
} from '@mui/material';
import {
  Send,
  CalendarToday,
  Palette,
  Cloud,
  Devices,
  EmojiEvents,
  Restaurant,
  Star,
  Discount,
  Forum,
  Redeem,
  Construction
} from '@mui/icons-material';
import Navigation from '../components/Navigation';
import { betaContent } from '../content/pages/beta';

// Icon mapping
const IconMap: Record<string, React.ReactElement> = {
  CalendarToday: <CalendarToday sx={{ fontSize: 32 }} />,
  Palette: <Palette sx={{ fontSize: 32 }} />,
  Cloud: <Cloud sx={{ fontSize: 32 }} />,
  Devices: <Devices sx={{ fontSize: 32 }} />,
  EmojiEvents: <EmojiEvents sx={{ fontSize: 32 }} />,
  Restaurant: <Restaurant sx={{ fontSize: 32 }} />,
  Star: <Star sx={{ fontSize: 28 }} />,
  Discount: <Discount sx={{ fontSize: 28 }} />,
  Forum: <Forum sx={{ fontSize: 28 }} />,
  Redeem: <Redeem sx={{ fontSize: 28 }} />
};

const Beta: React.FC = () => {
  const c = betaContent;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    useCase: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const config = window.__APP_CONFIG__;
      const baseUrl = config?.API_BASE_URL || 'http://localhost:8080/api';

      const response = await fetch(`${baseUrl}/api/beta/interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', useCase: '' });
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.error?.message || c.form.errorMessage);
        setSubmitStatus('error');
      }
    } catch {
      setErrorMessage(c.form.errorMessage);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: 8,
        bgcolor: 'primary.main',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Beaver Background Image */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/images/beaver-happy-family.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.15,
          zIndex: 0,
        }} />

        <Navigation />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              {c.hero.title}
            </Typography>
            <Typography variant="h5" component="h2" sx={{ opacity: 0.95, maxWidth: 700, mx: 'auto', lineHeight: 1.5, mb: 3 }}>
              {c.hero.subtitle}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.6 }}>
              {c.hero.description}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Why This Matters */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
          {c.opportunity.title}
        </Typography>
        {c.opportunity.paragraphs.map((para, i) => (
          <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.15rem', lineHeight: 1.8, color: 'text.secondary', textAlign: 'center' }}>
            {para}
          </Typography>
        ))}
      </Container>

      {/* What's Ready Now */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              {c.whatsReady.title}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {c.whatsReady.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {c.whatsReady.features.map((feature, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Card sx={{ height: '100%', p: 3, textAlign: 'center', boxShadow: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    {IconMap[feature.iconName]}
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What We're Building Together */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            {c.building.title}
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            {c.building.subtitle}
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ maxWidth: 900, mx: 'auto' }}>
          {c.building.features.map((feature, i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card sx={{
                height: '100%',
                p: 4,
                boxShadow: 3,
                border: 2,
                borderColor: 'primary.light',
                borderRadius: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    {IconMap[feature.iconName]}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
                  {feature.description}
                </Typography>
                <Chip
                  icon={<Construction sx={{ fontSize: 16 }} />}
                  label={feature.status}
                  size="small"
                  sx={{
                    bgcolor: '#ff9800',
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary', fontStyle: 'italic', fontSize: '1.1rem' }}>
          {c.building.cta}
        </Typography>
      </Container>

      {/* Benefits */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 600, mb: 6 }}>
            {c.benefits.title}
          </Typography>

          <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto' }}>
            {c.benefits.items.map((item, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                    {IconMap[item.iconName]}
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {item.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Sign Up Form */}
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, boxShadow: 4, borderRadius: 3 }}>
          <Typography variant="h4" textAlign="center" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
            {c.form.title}
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ color: 'text.secondary', mb: 4 }}>
            {c.form.subtitle}
          </Typography>

          {submitStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {c.form.successMessage}
            </Alert>
          )}

          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={c.form.useCaseLabel}
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  placeholder={c.form.useCasePlaceholder}
                  required
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic', fontSize: '0.85rem' }}>
                  {c.form.consent}
                </Typography>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  disabled={isSubmitting}
                  sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                >
                  {isSubmitting ? c.form.submittingButton : c.form.submitButton}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
            {c.pricing.note}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Beta;
